import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import navigationItemReferences from "../../../constants/navigation-item-references";
import { getDataGridFakeData } from "../../../helpers/coappDataGridUtils";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../hooks/useDatagridSorting";
import useToastAlert from "../../../hooks/useToastAlert";
import { selectOrganizationIdentityProvider, selectOrganizationSsoAndScim } from "../../../redux/userSlice";
import CoAppDatagridHeader from "../../global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "../../global/components/datagrid/CoAppStandardDataGrid";
import CoAppCreateGroupOrRoleModal from "../../global/components/modals/CoAppCreateGroupOrRoleModal";
import CoAppDestructiveConfirmationModal from "../../global/components/modals/CoAppDestructiveConfirmationModal";

import { getGroupManagementColumns, groupColumns, ssoAndScimEnabledGroupColumns } from "./columns/group-management-columns";

export default function GroupManagement() {
	const organizationSsoAndScim = useSelector(selectOrganizationSsoAndScim);
	const dummyRows = getDataGridFakeData(organizationSsoAndScim ? ssoAndScimEnabledGroupColumns : groupColumns);
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("groups");
	const { openResourceEditPage } = useCoAppNavigation();
	const identityProviderName = useSelector(selectOrganizationIdentityProvider);

	const [groupSeed, setGroupSeed] = useState(0);
	const [organizationGroups, setOrganizationGroups] = useState(dummyRows);
	const [groupCreateDialogOpen, setGroupCreateDialogOpen] = useState(false);
	const [groupDeleteDialogOpen, setGroupDeleteDialogOpen] = useState(false);
	const [groupBeingDeleted, setGroupBeingDeleted] = useState({ name: "" });
	const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
	const [groupNameError, setGroupNameError] = useState("");
	const [groupName, setGroupName] = useState("");
	const [selectedGroups, setSelectedGroups] = useState([]);
	const [loading, setLoading] = useState(true);

	const handleToggleGroupDeleteDialog = (group = { name: "" }) => {
		setGroupDeleteDialogOpen(!groupDeleteDialogOpen);
		setGroupBeingDeleted(group);
	};

	const duplicateGroup = (groupId) => {
		axios
			.post(apiRoutes.copyGroup(groupId), {}, { headers: authHeader })
			.then(() => {
				setGroupSeed(Math.random());
			})
			.catch((err) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_DUPLICATION_ERROR_MSG);
			});
	};

	const columns = useMemo(() => (
		getGroupManagementColumns(
			loading,
			openResourceEditPage,
			navigationItemReferences,
			duplicateGroup,
			handleToggleGroupDeleteDialog,
			identityProviderName,
			organizationSsoAndScim
		)
	), [loading, identityProviderName, organizationSsoAndScim, organizationGroups]);

	const createNewGroup = () => {
		if (!groupName) {
			setGroupNameError("Required *");
			return;
		}

		let groupJSON = {
			"groupName": groupName,
		};

		axios.post(apiRoutes.createGroup, groupJSON, { headers: authHeader })
			.then(() => {
				setGroupSeed(Math.random());
				setGroupCreateDialogOpen(false);
				setGroupName("");
				setGroupNameError("");
				handleToastAlert(messageLevels.SUCCESS, messages.GROUP_CREATED_SUCCESS_MSG);
			})
			.catch((err) => {
				if (err.response.data.message.includes("already exists")) {
					setGroupNameError(messages.RESOURCE_ALREADY_EXISTS_ERROR_MSG("Group"));
					return;
				}
				handleToastAlert(messageLevels.ERROR, messages.GROUP_CREATION_ERROR_MSG);
			});
	};

	const handleToggleGroupCreateDialog = () => {
		setGroupCreateDialogOpen(!groupCreateDialogOpen);
		setGroupName("");
		setGroupNameError("");
	};

	const handleGroupNameChange = (e) => {
		setGroupName(e.target.value);
		setGroupNameError("");
	};

	const deleteGroup = () => {
		axios
			.delete(apiRoutes.deleteGroup(groupBeingDeleted.id), {
				headers: authHeader,
			})
			.then(() => {
				handleToastAlert(messageLevels.INFO, messages.GROUP_DELETED_SUCCESS_MSG(false));
				setGroupSeed(Math.random());
				handleToggleGroupDeleteDialog();
			})
			.catch((err) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_DELETION_ERROR_MSG);
				handleToggleGroupDeleteDialog();
			});
	};

	const bulkDeleteGroups = () => {
		const deleteData = {
			groupIds: selectedGroups
		};
		axios.delete(apiRoutes.bulkDeleteGroups, { headers: authHeader, data: deleteData })
			.then(() => {
				handleToastAlert(messageLevels.INFO, messages.GROUP_DELETED_SUCCESS_MSG(selectedGroups.length > 1));
				setGroupSeed(Math.random());
				setBulkDeleteDialogOpen(false);
			})
			.catch((err) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_DELETION_ERROR_MSG);
				setBulkDeleteDialogOpen(false);
			});
	};


	const initGroupManagement = () => {
		axios.get(apiRoutes.getGroups, { headers: authHeader })
			.then((res) => {
				console.log(res.data);
				setOrganizationGroups(res.data);
				setLoading(false);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	useEffect(() => {
		initGroupManagement();
		handleInitializeSortModel();
	}, [groupSeed]);

	return (
		<>
			<CoAppDatagridHeader
				title="Groups"
				resourceType="Group"
				addOnClickHandler={handleToggleGroupCreateDialog}
				addIsPresent={true}
				editIsPresent={false}
				deleteIsPresent={false}
			/>
			<CoAppStandardDataGrid
				rows={organizationGroups}
				columns={columns}
				pinnedColumns={["actions", "__check__", "name"]}
				targetResource="groups"
				selectionModel={selectedGroups}
				setSelectionModel={setSelectedGroups}
				handleModalToggle={() => setBulkDeleteDialogOpen(prev => !prev)}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
			/>
			<CoAppCreateGroupOrRoleModal
				dialogOpen={groupCreateDialogOpen}
				changeHandler={handleGroupNameChange}
				confirmClickHandler={createNewGroup}
				cancelClickHandler={handleToggleGroupCreateDialog}
				nameError={groupNameError}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={groupDeleteDialogOpen}
				dialogTitle={`Delete ${groupBeingDeleted.name} group?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(groupBeingDeleted.name, "group")}
				confirmClickHandler={deleteGroup}
				cancelClickHandler={handleToggleGroupDeleteDialog}
				actionText="Delete"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={bulkDeleteDialogOpen}
				dialogTitle={`Delete selected ${selectedGroups.length > 1 ? "groups" : "group"}?`}
				dialogMessage={"Are you sure you want to bulk delete all the selected groups? This action cannot be undone."}
				confirmClickHandler={bulkDeleteGroups}
				cancelClickHandler={() => setBulkDeleteDialogOpen(false)}
				actionText="Delete"
			/>
		</>
	);
}